import { Award, CheckCircle2, Target, Eye, Heart, Users, Lightbulb } from 'lucide-react'
import AnimatedSection, { StaggerContainer, StaggerItem } from '@/components/AnimatedSection'

const values = [
  { icon: Heart, title: 'Integrity', description: 'We uphold the highest ethical standards in every survey we conduct.' },
  { icon: Target, title: 'Precision', description: 'Every measurement is taken with meticulous care and verified for accuracy.' },
  { icon: Lightbulb, title: 'Innovation', description: 'We embrace new technologies to deliver faster, more accurate results.' },
  { icon: Users, title: 'Client Focus', description: 'Your success is our priority. We tailor solutions to your specific needs.' },
]

const certifications = [
  { name: 'SURCON (Surveyors Council of Nigeria)', image: '/images/others/surcon.jpg' },
  { name: 'Member of Nigerian Institution of Surveyors', image: '/images/others/nis.jpg' },
  { name: 'Association of Private Practicing Surveyors of Nigeria', image: '/images/others/appsn.jpg' },
]

export default function About() {
  return (
    <div className="min-h-screen pt-32">
      {/* Page Hero */}
      <section className="py-20 bg-cream">
        <div className="section-padding max-w-7xl mx-auto text-center">
          <AnimatedSection>
            <span className="text-accent font-medium text-sm uppercase tracking-wider">About Us</span>
            <h1 className="heading-lg text-black mt-3 mb-4 font-serif">About Advict Spatial Consult</h1>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              A Lagos-based professional surveying firm delivering accurate, reliable geospatial solutions.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/others/land-survey.png"
                  alt="Our Story"
                  className="w-full h-[450px] object-cover"
                />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="heading-md text-black mt-3 mb-6 font-serif">Precision, Innovation &amp; Client Satisfaction</h2>
              <p className="text-neutral-600 leading-relaxed mb-4">
                Advict Spatial Consult is a Lagos-based professional surveying firm offering
                various geospatial services. With years of industry experience, we specialise in
                delivering accurate and reliable survey solutions tailored to the needs of both
                private and public sector clients.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-4">
                Our operations are built on precision, innovation, and client satisfaction. We
                combine sound surveying practice with modern geospatial technology to give every
                client dependable information and practical solutions.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {['Private & Public Sector', 'Reliable Survey Solutions', 'Modern Geospatial Technology', 'Professional Integrity'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-neutral-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-surface">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <AnimatedSection>
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-100 h-full">
                <div className="w-14 h-14 bg-accent/20 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-accent-dark" />
                </div>
                <h3 className="text-2xl font-semibold text-black mb-4 font-serif">Our Mission</h3>
                <p className="text-neutral-600 leading-relaxed">
                  To provide high-quality, cost-effective, and timely surveying services using
                  cutting-edge technology, while maintaining the highest standards of
                  professionalism and integrity.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-100 h-full">
                <div className="w-14 h-14 bg-accent/20 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-accent-dark" />
                </div>
                <h3 className="text-2xl font-semibold text-black mb-4 font-serif">Our Vision</h3>
                <p className="text-neutral-600 leading-relaxed">
                  To be recognised as a leading geospatial solutions provider in Nigeria and
                  across West Africa.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="flex flex-wrap items-end justify-between gap-4 border-b border-neutral-200 pb-7">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Our Values</span>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-black sm:text-5xl">What We Stand For</h2>
            </div>
            <p className="max-w-2xl text-sm leading-7 text-neutral-600 sm:text-base">
              Clear standards guide every decision we make—from first site inspection to the final deliverable.
            </p>
          </AnimatedSection>

          <StaggerContainer className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {values.map((value, index) => (
              <StaggerItem key={value.title}>
                <article className="group h-full rounded-xl border border-neutral-200 p-5 transition-colors duration-300 hover:bg-cream/60">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-2xl text-accent-dark">0{index + 1}</span>
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-accent">
                      <value.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-black">{value.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-neutral-600">{value.description}</p>
                </article>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-surface">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Team</span>
            <h2 className="heading-md text-black mt-3 mb-4 font-serif">Meet Our Experts</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Providing the leadership and expertise behind Advict Spatial Consult.
            </p>
          </AnimatedSection>
          <StaggerContainer>
            <StaggerItem>
              <article className="max-w-5xl mx-auto grid md:grid-cols-[minmax(250px,360px)_1fr] bg-white rounded-2xl overflow-hidden shadow-sm">
                <img
                  src="/images/team/Tolu.jpg"
                  alt="Tolu, Chief Executive Officer of Advict Spatial Consult"
                  className="w-full h-full min-h-80 object-cover"
                />
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-3xl font-semibold text-black font-serif">Surv. Tolulope Adeleke</h3>
                  <span className="text-accent font-medium text-sm uppercase tracking-wider mt-2 mb-5">Chief Executive Officer</span>
                  <div className="space-y-4 text-neutral-600 leading-relaxed">
                    <p>
                      Surv. Tolulope Adeleke leads Advict Spatial Consult with a commitment to
                      precision, integrity, and practical solutions for every client.
                    </p>
                    <p>
                      His leadership brings together sound surveying practice, modern geospatial
                      technology, and a clear understanding of the needs behind every project.
                    </p>
                    <p>
                      With a focus on quality and innovation, he guides the team in delivering
                      dependable services that help clients move their projects forward with
                      confidence.
                    </p>
                  </div>
                </div>
              </article>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-black text-white">
        <div className="section-padding max-w-7xl mx-auto text-center">
          <AnimatedSection>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2 text-accent font-medium text-sm">
              <Award className="w-4 h-4" />
              Certifications
            </span>
            <h2 className="heading-md text-white mt-5 mb-3 font-serif">Licensed &amp; Certified</h2>
            <p className="text-cream/80 max-w-2xl mx-auto mb-12 leading-relaxed">
              We meet all regulatory requirements and maintain the highest professional standards.
            </p>
          </AnimatedSection>
          <StaggerContainer className="grid md:grid-cols-3 gap-5 text-left">
            {certifications.map((cert) => (
              <StaggerItem key={cert.name}>
                <div className="h-full rounded-2xl border border-white/15 bg-white/5 p-4 sm:p-5 flex items-center gap-4 transition-colors duration-300 hover:border-accent/70 hover:bg-white/10">
                  <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-white p-1">
                    <img src={cert.image} alt={`${cert.name} logo`} className="w-full h-full object-contain" />
                  </div>
                  <h3 className="text-base sm:text-lg leading-relaxed font-semibold text-white font-serif">
                    {cert.name}
                  </h3>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </div>
  )
}
